import { Link } from "react-router-dom";
import { AppLink } from "@/components/AppLink";

/** FAQ categories with content copied from FAQ.tsx (About Perch Mobile, Making the Switch, Technical Support, Billing & Payments) */
export const FAQ_CATEGORIES = [
  {
    id: "about",
    label: "Who Is Perch Mobile?",
    headline: "Who Is Perch Mobile?",
    items: [
      {
        id: "about-1",
        question: "What is Perch Mobile?",
        answer: (
          <div className="space-y-3">
            <p>Perch Mobile is a new type of mobile company, one that puts the control in your hands. Perch was founded on a simple idea: what if your monthly phone bill actually invested in your future? What if the service you pay for every month helped you build credit, what if you paid for only the data you actually used, and you were in complete control?</p>
            <p>That's why we built the Perch mobile service differently.</p>
            <p>We run on AT&T's trusted 5G network with plans starting at $9.99/month. If you opt in to our FREE credit building partner program, every on-time payment builds your credit history. And you're never locked in, change your plan or cancel anytime.</p>
            <p>We're built for self investors who care about where their money goes, and we're here to invest in you.</p>
            <p>Mobile service designed around your life, built for your future.</p>
            <p className="font-semibold text-white/90">Your money. Your data. Your future.</p>
          </div>
        ),
      },
      {
        id: "about-2",
        question: "What network does Perch use?",
        answer: <p>5G/4G LTE speeds on AT&T's trusted network. Same towers. Real pricing.</p>,
      },
      {
        id: "about-3",
        question: "What services do you offer?",
        answer: (
          <p>Month-to-month wireless plans with unlimited talk and text, plus data options from 1GB to Unlimited. We also offer International plans so you can be connected on the go and plans for your wearables. Check out all our plans at <Link to="/plans" className="text-accent hover:underline">our plans page</Link>.</p>
        ),
      },
    ],
  },
  {
    id: "switch",
    label: "How Do I Make The Switch To Perch Mobile?",
    headline: "How do I make the switch to Perch Mobile?",
    items: [
      {
        id: "switch-1",
        question: "How do I switch to Perch?",
        answer: (
          <div className="space-y-3">
            <p>Head to our <Link to="/plans" className="text-accent hover:underline">plans page</Link> to choose the plan that fits your life.</p>
            <p>Once you select the right plan for you, simply click "Start Building" and we will take you to a basic summary of the plan you chose.</p>
            <p>If everything looks good, continue to checkout and purchase your plan.</p>
            <p>Once completed, you receive instructions on how to access your Perch account and complete the activation process. It's all very straightforward!</p>
          </div>
        ),
      },
      {
        id: "switch-2",
        question: "Can I keep my current phone number?",
        answer: (
          <div className="space-y-3">
            <p>Yes! This is called porting, and it's super simple. Your old carrier stays active until the switch completes, so you won't lose service.</p>
            <p><AppLink to="/activation-guide" className="text-accent hover:underline">View Porting Instructions →</AppLink></p>
          </div>
        ),
      },
      {
        id: "switch-3",
        question: "Do I need a new phone?",
        answer: (
          <p>We doubt you do. Almost all phones work with Perch as long as they're unlocked. Enter your IMEI at perchmobile.com to check compatibility—it takes two seconds.</p>
        ),
      },
      {
        id: "switch-4",
        question: "Will I lose my contacts, photos, and text messages?",
        answer: (
          <div className="space-y-3">
            <p>Nope. Back up your phone before switching to be safe, but everything on your phone should stay the same when you transfer over. Your contacts, photos, and texts stay right where they are.</p>
            <p><strong className="text-white/90">Note:</strong> Voicemails from your old carrier won't automatically transfer, so save any important ones before you switch.</p>
          </div>
        ),
      },
      {
        id: "switch-5",
        question: "Does my phone need to be unlocked?",
        answer: (
          <div className="space-y-3">
            <p>Your phone needs to be unlocked to switch to Perch. Check your phone's settings or contact your current carrier to verify.</p>
            <p><AppLink to="/activation-guide" className="text-accent hover:underline">See unlock instructions for iPhone & Android →</AppLink></p>
          </div>
        ),
      },
    ],
  },
  {
    id: "imei",
    label: "How To Find My IMEI?",
    headline: "How to find my IMEI?",
    items: [
      {
        id: "tech-1",
        question: "How do I find my IMEI?",
        answer: (
          <p>The easiest way works on any phone: Open your phone app and dial <strong className="text-white/90">*#06#</strong>. Your IMEI appears instantly.</p>
        ),
      },
      {
        id: "tech-2",
        question: "How do I find my IMEI on my iPhone?",
        answer: (
          <div className="space-y-3">
            <div className="p-4 rounded-lg bg-white/10">
              <p className="font-medium text-white/90">Fastest method - Works on any iPhone:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Open your Phone app</li>
                <li>Dial *#06#</li>
                <li>Your IMEI appears on screen</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-white/10">
              <p className="font-medium text-white/90">In Settings:</p>
              <ol className="list-decimal list-inside space-y-1 mt-2">
                <li>Go to Settings</li>
                <li>Tap General</li>
                <li>Tap About</li>
                <li>Scroll down to find your IMEI/MEID or ICCID</li>
                <li>Touch and hold any number to copy it</li>
              </ol>
            </div>
            <div className="p-4 rounded-lg bg-white/10">
              <p className="font-medium text-white/90">Using Finder or iTunes:</p>
              <ol className="list-decimal list-inside space-y-1 mt-2">
                <li>Connect your iPhone to your computer</li>
                <li>Mac (Catalina or later): Open Finder</li>
                <li>Mac (Mojave or earlier) or PC: Open iTunes</li>
                <li>Locate your device</li>
                <li>Find your IMEI under General (Finder) or Summary (iTunes)</li>
              </ol>
            </div>
            <div className="p-4 rounded-lg bg-white/10">
              <p className="font-medium text-white/90">Don't have your device handy?</p>
              <ol className="list-decimal list-inside space-y-1 mt-2">
                <li>Go to appleid.apple.com</li>
                <li>Sign in with your Apple ID</li>
                <li>Choose the Devices section</li>
                <li>Select your device to see the IMEI</li>
              </ol>
            </div>
          </div>
        ),
      },
      {
        id: "tech-3",
        question: "How do I find my IMEI on my iPad?",
        answer: (
          <div className="space-y-3">
            <p><strong className="text-white/90">For iPad (Wi-Fi + Cellular models):</strong></p>
            <div className="p-4 rounded-lg bg-white/10">
              <p className="font-medium text-white/90">Fastest method:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Open your Phone app (if available)</li>
                <li>Dial *#06#</li>
                <li>Your IMEI appears on screen</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-white/10">
              <p className="font-medium text-white/90">On the device itself:</p>
              <p className="mt-1">Look on the back of your iPad. The serial number and IMEI are printed there.</p>
            </div>
            <div className="p-4 rounded-lg bg-white/10">
              <p className="font-medium text-white/90">In Settings:</p>
              <ol className="list-decimal list-inside space-y-1 mt-2">
                <li>Go to Settings</li>
                <li>Tap General</li>
                <li>Tap About</li>
                <li>Scroll down to find your IMEI</li>
                <li>Touch and hold the number to copy it</li>
              </ol>
            </div>
            <div className="p-4 rounded-lg bg-white/10">
              <p className="font-medium text-white/90">Using Finder or iTunes:</p>
              <ol className="list-decimal list-inside space-y-1 mt-2">
                <li>Connect your iPad to your computer</li>
                <li>Mac (Catalina or later): Open Finder</li>
                <li>Mac (Mojave or earlier) or PC: Open iTunes</li>
                <li>Locate your device</li>
                <li>Click Serial Number to find the IMEI</li>
              </ol>
            </div>
          </div>
        ),
      },
      {
        id: "tech-4",
        question: "How do I find my IMEI on my Android?",
        answer: (
          <div className="space-y-3">
            <div className="p-4 rounded-lg bg-white/10">
              <p className="font-medium text-white/90">Fastest method - Works on any Android:</p>
              <ul className="list-disc list-inside mt-2 space-y-1">
                <li>Open your Phone app</li>
                <li>Dial *#06#</li>
                <li>Your IMEI appears on screen</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-white/10">
              <p className="font-medium text-white/90">Check IMEI from Settings:</p>
              <ol className="list-decimal list-inside space-y-1 mt-2">
                <li>Open Settings</li>
                <li>Tap About phone</li>
                <li>Scroll down to find your IMEI</li>
              </ol>
              <p className="mt-2">OR:</p>
              <ol className="list-decimal list-inside space-y-1 mt-2">
                <li>Open Settings</li>
                <li>Tap About phone</li>
                <li>Tap "Detailed info and specs"</li>
                <li>Your IMEI is listed there</li>
              </ol>
            </div>
            <p className="text-sm"><strong className="text-white/90">Note:</strong> The exact steps may vary slightly depending on your Android device manufacturer.</p>
          </div>
        ),
      },
    ],
  },
  {
    id: "billing",
    label: "Billing, Taxes, And Fees",
    headline: "Billing, taxes, and fees",
    items: [
      {
        id: "billing-1",
        question: "How does billing work?",
        answer: (
          <div className="space-y-3">
            <p>All plans are month-to-month and paid in advance. When you sign up, no matter the day of the month, we charge you a full monthly payment. On your next month's bill, we prorate the amount.</p>
            <p>For instance, if you sign up on the 15th of the month for a $9.99 plan, you will get charged that amount, however the following month, you would get a credit for 14 days of service. After that, billing happens on the 1st of every month.</p>
          </div>
        ),
      },
      {
        id: "billing-2",
        question: "What payment methods do you accept?",
        answer: <p>Credit cards, debit cards, and ACH payments. Autopay is required for all Perch Mobile plans.</p>,
      },
      {
        id: "billing-3",
        question: "Where can I view my bill?",
        answer: (
          <p>Log into your <AppLink to="/customer-portal" className="text-accent hover:underline">Perch Dashboard</AppLink> to view your bill anytime.</p>
        ),
      },
      {
        id: "billing-4",
        question: "When will my bill be available?",
        answer: <p>Bills are available on the 1st of every month.</p>,
      },
    ],
  },
];
